import * as v from "valibot";
import { baseVals, defineRecordSchema, defineValidation, listVals, validations } from "../../common/validation.common";
import {
    CustomerCategoryEnum,
    ProvisionItemStatusEnum,
    ProvisionSpecStatusEnum,
    RegistrationStatusEnum,
} from "../../data/enums";

export namespace CustomerRegistrationValidation {
    export const Record = defineValidation(
        defineRecordSchema({
            filters: v.object({
                status: v.optional(v.array(v.picklist(RegistrationStatusEnum.values))),
            }),
        }),
    );

    export const Create = defineValidation(
        v.object({
            name: baseVals.string(),
            phone: baseVals.phone(),
            email: v.optional(baseVals.email()),
            address: baseVals.string(),
            location: baseVals.coordinate(),
            identityNumber: v.optional(baseVals.identityNumber()),
            identityCardFile: v.optional(baseVals.file("Foto Kartu Identitas (KTP / SIM)")),
            productId: v.optional(baseVals.string()),
            marketingId: v.optional(baseVals.string()),
        }),
    );

    export const Signup = defineValidation(
        v.object({
            name: baseVals.string(),
            phone: baseVals.phone(),
            email: v.optional(baseVals.email()),
            address: baseVals.string(),
            location: baseVals.coordinate(),
            identityNumber: baseVals.string(),
            identityCardFile: baseVals.string(),
            productId: v.optional(baseVals.string()),
            refcode: baseVals.string(),
        }),
    );

    export const Update = defineValidation(
        v.object({
            name: v.exactOptional(baseVals.string()),
            phone: v.exactOptional(baseVals.phone()),
            email: v.exactOptional(baseVals.email()),
            address: v.exactOptional(baseVals.string()),
            identityNumber: v.exactOptional(baseVals.identityNumber()),
            location: v.exactOptional(baseVals.coordinate()),
            priority: v.exactOptional(baseVals.intRange(1, 10, "Prioritas")),
            surveyData: v.exactOptional(
                v.object({
                    schedule: v.optional(baseVals.isoDate("Jadwal Survei")),
                    supervisorId: v.optional(baseVals.string()),
                    technicianIds: v.optional(listVals.stringList()),
                    ccIds: v.optional(listVals.stringListEmptiable()),
                    notes: v.optional(baseVals.stringEmptiable()),
                    topology: v.optional(baseVals.string()),
                }),
            ),
            installationData: v.exactOptional(
                v.object({
                    schedule: v.optional(baseVals.isoDate("Jadwal Instalasi")),
                    cost: v.optional(baseVals.intMin(0, "Biaya Instalasi")),
                    supervisorId: v.optional(baseVals.string()),
                    technicianIds: v.optional(listVals.stringList()),
                    ccIds: v.optional(listVals.stringListEmptiable()),
                    notes: v.optional(baseVals.stringEmptiable()),
                    parameter: v.optional(baseVals.string()),
                }),
            ),
            provisionItemIds: v.exactOptional(listVals.stringListEmptiable()),
        }),
    );

    export const Remove = defineValidation(
        v.object({
            email: validations.deletable,
            surveyData: v.optional(
                v.object({
                    ccIds: validations.deletable,
                }),
            ),
            installationData: v.optional(
                v.object({
                    ccIds: validations.deletable,
                }),
            ),
        }),
    );

    export const RegistrationSetup = defineValidation(
        v.object({
            priority: baseVals.intRange(1, 10, "Prioritas"),
            doSurvey: v.boolean(),
        }),
    );

    export const SurveyCreate = defineValidation(
        v.object({
            schedule: baseVals.isoDate("Jadwal Survei"),
            supervisorId: baseVals.string(),
            technicianIds: listVals.stringList(),
            ccIds: v.optional(listVals.stringListEmptiable()),
        }),
    );

    export const SurveyReport = defineValidation(
        v.object({
            notes: v.optional(baseVals.stringEmptiable()),
            topology: v.optional(baseVals.string()),
        }),
    );

    export const CustomerCreate = defineValidation(
        v.object({
            area: v.optional(baseVals.string()),
            notes: v.optional(baseVals.stringEmptiable()),
            category: v.optional(v.picklist(CustomerCategoryEnum.values)),
        }),
    );

    export const InstallationCreate = defineValidation(
        v.object({
            schedule: baseVals.isoDate("Jadwal Instalasi"),
            cost: baseVals.intMin(0, "Biaya Instalasi"),
            supervisorId: baseVals.string(),
            technicianIds: listVals.stringList(),
            ccIds: v.optional(listVals.stringListEmptiable()),
        }),
    );

    export const ProvisionCreate = defineValidation(
        v.object({
            warehouseId: baseVals.string(),
            specs: v.pipe(
                v.array(
                    v.object({
                        name: baseVals.string(),
                        status: v.picklist(ProvisionSpecStatusEnum.values),
                        quantity: baseVals.intMin(1, "Qty"),
                        notes: v.optional(baseVals.stringEmptiable()),
                    }),
                ),
                v.minLength(1),
            ),
        }),
    );

    export const ProvisionCancel = defineValidation(
        v.object({
            provisionId: baseVals.string(),
        }),
    );

    export const InstallationReport = defineValidation(
        v.object({
            notes: v.optional(baseVals.stringEmptiable()),
            parameter: v.optional(baseVals.string()),
            provisionItemIds: listVals.stringListEmptiable(),
        }),
    );

    export const ProvisionFinalize = defineValidation(
        v.object({
            items: v.array(
                v.object({
                    id: baseVals.string(),
                    status: v.picklist(ProvisionItemStatusEnum.values),
                    price: v.optional(baseVals.intMin(0, "Harga")),
                }),
            ),
        }),
    );

    export const FinalizationCreate = defineValidation(
        v.object({
            deactivateRadius: v.boolean(),
        }),
    );

    export const FinalizationFinish = defineValidation(
        v.object({
            employeeSignature: baseVals.string(),
            customerSignature: baseVals.string(),
        }),
    );

    export const GenerateExport = defineValidation(
        v.object({
            minDate: v.optional(baseVals.isoDate()),
            maxDate: v.optional(baseVals.isoDate()),
        }),
    );

    export namespace API {
        export const Update = defineValidation(
            v.object({
                installationData: v.optional(
                    v.object({
                        notes: v.optional(baseVals.stringEmptiable()),
                        parameter: v.optional(baseVals.string()),
                    }),
                ),
            }),
        );

        export const Signup = defineValidation(
            v.object({
                name: baseVals.string(),
                phone: baseVals.phone(),
                email: v.optional(baseVals.email()),
                address: baseVals.string(),
                location: baseVals.coordinate(),
                identityNumber: baseVals.string(),
                identityCardFile: baseVals.string(),
                productId: v.optional(baseVals.string()),
            }),
        );
    }
}
